import type { CartItem } from '../CartModal';
import type { cartTranslations } from '@/i18n';

interface CartItemCardProps {
  item: CartItem;
  t: typeof cartTranslations['ko'];
  formatCurrency: (amount: number) => string;
  onQuantityChange: (itemId: string, change: number) => void;
  onEditItem: (item: CartItem) => void;
  onDeleteClick: (itemId: string) => void;
}

/**
 * 장바구니 아이템 카드 컴포넌트
 * - 아이템 정보 표시
 * - 수량 조절 버튼 (+/-)
 * - 옵션/커스터마이제이션 표시
 * - 가격 표시
 * - 삭제/수정 버튼
 */
export function CartItemCard({
  item,
  t,
  formatCurrency,
  onQuantityChange,
  onEditItem,
  onDeleteClick,
}: CartItemCardProps) {
  return (
    <div className="bg-background rounded-[clamp(12px,3vw,16px)] p-[clamp(16px,4vw,20px)] shadow-sm border border-border">
      {/* 헤더: 메뉴명 + 삭제 버튼 */}
      <div className="flex items-center justify-between mb-[clamp(8px,2vw,12px)]">
        <h3 className="font-['Noto_Sans:Medium',_sans-serif] text-[clamp(14px,3.5vw,16px)] text-foreground leading-tight pr-[clamp(8px,2vw,12px)] font-bold">
          {item.name}
        </h3>
        <button
          onClick={() => onDeleteClick(item.id)}
          className="text-muted-foreground hover:text-destructive transition-colors duration-200 p-[clamp(2px,0.5vw,4px)]"
        >
          <svg className="w-[clamp(14px,3.5vw,16px)] h-[clamp(14px,3.5vw,16px)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* 컨트롤 행 */}
      <div className="flex items-center justify-between gap-[clamp(8px,2vw,12px)]">
        {/* 수량 조절 컨트롤 */}
        <div className="flex items-center bg-muted rounded-full px-[clamp(4px,1vw,6px)] py-[clamp(2px,0.5vw,3px)]">
          <button
            onClick={() => onQuantityChange(item.id, -1)}
            className="w-[clamp(20px,5vw,24px)] h-[clamp(20px,5vw,24px)] rounded-full flex items-center justify-center hover:bg-border transition-colors"
          >
            <svg className="w-[clamp(10px,2.5vw,12px)] h-[clamp(10px,2.5vw,12px)] text-secondary" fill="none" viewBox="0 0 24 24">
              <path d="M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>

          <span className="font-['Noto_Sans:Medium',_sans-serif] text-[clamp(14px,3.5vw,16px)] text-foreground min-w-[clamp(20px,5vw,24px)] text-center">
            {item.quantity}
          </span>

          <button
            onClick={() => onQuantityChange(item.id, 1)}
            className="w-[clamp(20px,5vw,24px)] h-[clamp(20px,5vw,24px)] rounded-full flex items-center justify-center hover:bg-border transition-colors"
          >
            <svg className="w-[clamp(10px,2.5vw,12px)] h-[clamp(10px,2.5vw,12px)] text-secondary" fill="none" viewBox="0 0 24 24">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="flex items-center gap-[clamp(8px,2vw,12px)]">
          {/* 변경 버튼 */}
          <button
            onClick={() => onEditItem(item)}
            className="text-[clamp(12px,3vw,14px)] text-secondary hover:text-secondary-dark font-['Noto_Sans:Medium',_sans-serif] underline underline-offset-2 transition-colors"
          >
            {t.change}
          </button>

          {/* 가격 표시 */}
          <span className="font-['Noto_Sans:Bold',_sans-serif] text-[clamp(14px,3.5vw,16px)] text-foreground">
            {formatCurrency(item.price * item.quantity)}
          </span>
        </div>
      </div>

      {/* 옵션 목록 */}
      {item.customizations && item.customizations.length > 0 && (
        <CartItemOptions
          customizations={item.customizations}
          itemId={item.id}
          t={t}
          formatCurrency={formatCurrency}
        />
      )}
    </div>
  );
}

interface CartItemOptionsProps {
  customizations: string[];
  itemId: string;
  t: typeof cartTranslations['ko'];
  formatCurrency: (amount: number) => string;
}

/**
 * 장바구니 아이템 옵션 목록 컴포넌트
 */
function CartItemOptions({
  customizations,
  itemId,
  t,
  formatCurrency,
}: CartItemOptionsProps) {
  return (
    <div className="mt-[clamp(12px,3vw,16px)] p-[0px] border-t border-border">
      <div className="space-y-[clamp(3px,0.8vw,5px)]">
        {customizations.filter(Boolean).map((customization, index) => {
          // 옵션 정보를 파싱 - "카테고리: 옵션 이름 (+가격)" 또는 "옵션 이름 (+가격)" 형태
          const categoryMatch = customization?.match(/^(.+?):\s*(.+?)(?:\s*\(([+-]?\d+)\))?$/);
          const simpleMatch = customization?.match(/^(.+?)(?:\s*\(([+-]?\d+)\))?$/);

          let optionCategory = '';
          let optionName = '';
          let priceChange = 0;

          if (categoryMatch) {
            // "카테고리: 옵션명 (+가격)" 형태
            optionCategory = categoryMatch[1]?.trim() || '';
            optionName = categoryMatch[2]?.trim() || '';
            priceChange = categoryMatch[3] ? parseInt(categoryMatch[3]) : 0;
          } else if (simpleMatch) {
            // "옵션명 (+가격)" 형태
            optionName = simpleMatch[1]?.trim() || '';
            priceChange = simpleMatch[2] ? parseInt(simpleMatch[2]) : 0;
          } else {
            optionName = customization || '';
          }

          // 옵션 타입 감지 (필수/선택)
          const requiredCategories = ['고기 종류', '크기', '도우', '베이스', '온도', '소스'];
          const isRequired = requiredCategories.some(req =>
            optionCategory.includes(req) || optionName.includes(req)
          );

          return (
            <div
              key={`${itemId}-customization-${index}`}
              className="flex items-center justify-between transition-all duration-200 p-0"
            >
              <div className="flex items-center gap-[clamp(8px,2vw,10px)] flex-1 min-w-0">
                {/* 미니멀 인디케이터 */}
                <div className={`w-[clamp(6px,1.5vw,8px)] h-[clamp(6px,1.5vw,8px)] rounded-full shrink-0 ${
                  isRequired ? 'bg-secondary' : 'bg-primary'
                }`}></div>

                <div className="flex-1 min-w-0">
                  {/* 단일 라인으로 표시 */}
                  <div className="flex items-center gap-[clamp(4px,1vw,6px)]">
                    {optionCategory && (
                      <>
                        <span className="font-['Noto_Sans:Medium',_sans-serif] text-[clamp(10px,2.5vw,12px)] text-muted-foreground">
                          {optionCategory}
                        </span>
                        <span className="text-[clamp(8px,2vw,10px)] text-muted-foreground">-</span>
                      </>
                    )}
                    <span className="font-['Noto_Sans:Regular',_sans-serif] text-[clamp(11px,2.8vw,13px)] text-foreground truncate">
                      {optionName}
                    </span>
                  </div>
                </div>
              </div>

              {/* 간소화된 가격 표시 */}
              <div className="text-right shrink-0">
                {priceChange !== 0 ? (
                  <span className={`font-['Noto_Sans:Medium',_sans-serif] text-[clamp(10px,2.5vw,12px)] font-medium ${
                    priceChange > 0
                      ? isRequired ? 'text-secondary' : 'text-primary'
                      : 'text-destructive'
                  }`}>
                    {priceChange > 0 ? '+' : ''}{formatCurrency(priceChange)}
                  </span>
                ) : (
                  <span className="text-[clamp(9px,2.3vw,10px)] text-muted-foreground">
                    {t.default}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
